// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Alert, AppIcon, Button, Checkbox } from '@loa/ui-components';

export type BulkSelectionBarProps = Readonly<{
  /** Number of selected items */
  selectedCount: number;
  /** Total number of items that can be selected */
  totalCount: number;
  /** Whether all items are selected */
  isAllSelected: boolean;
  /** Callback to toggle select all */
  onSelectAllChange: (checked: boolean) => void;
  /** Callback to clear selection */
  onClearSelection: () => void;
  /** Callback for bulk reassign action */
  onBulkReassign?: () => void;
  /** Whether bulk actions are disabled */
  disabled?: boolean;
}>;

/**
 * BulkSelectionBar Component
 *
 * An action bar that appears when items are selected for bulk operations.
 * Shows selection count and provides bulk action buttons.
 *
 * Features:
 * - Select all checkbox
 * - Selected count display
 * - Bulk reassign action (optional)
 * - Clear selection button
 * - Only renders when items are selected
 */
export function BulkSelectionBar({
  selectedCount,
  totalCount,
  isAllSelected,
  onSelectAllChange,
  onClearSelection,
  onBulkReassign,
  disabled = false
}: BulkSelectionBarProps) {
  // Don't render if nothing is selected
  if (selectedCount === 0) {
    return null;
  }

  const itemText = selectedCount === 1 ? 'item' : 'items';

  return (
    <Alert
      variant="info"
      style={{ marginBottom: '1em' }}
      className="bulk-selection-bar"
      aria-label="Bulk selection actions"
    >
      <section
        aria-label="Bulk selection actions"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1em',
          flexWrap: 'wrap'
        }}
      >
        <Checkbox
          checked={isAllSelected}
          onChange={(e) => onSelectAllChange(e.target.checked)}
          disabled={disabled}
          label={`Select all ${totalCount}`}
          aria-label={`Select all ${totalCount} items`}
        />
        <span>
          <strong>{selectedCount}</strong> {itemText} selected
        </span>
        {onBulkReassign && (
          <Button
            variant="filled"
            size="sm"
            onClick={onBulkReassign}
            disabled={disabled}
            style={{ backgroundColor: '#2B1B4D' }}
            aria-label={`Bulk reassign ${selectedCount} ${itemText}`}
            iconLeft={<AppIcon name="exchange" />}
          >
            Bulk Reassign
          </Button>
        )}
        <Button
          variant="outlined"
          size="sm"
          onClick={onClearSelection}
          disabled={disabled}
          aria-label="Clear selection"
        >
          Clear Selection
        </Button>
      </section>
    </Alert>
  );
}

export default BulkSelectionBar;
